import type { EcommerceItem } from './types';

/**
 * Browser-lifetime state the GA4 funnel needs but no component can own.
 *
 * Two facts outlive the components that produce them:
 *
 * - which trips have already been reported as impressions for a search, so a
 *   remount of the results list does not report them again (the list is
 *   unmounted by `SlideInOut unmountOnExit` on every direction change, and
 *   remounts around hydration on a fast load);
 * - what is in the cart for each leg, so replacing a trip can report the
 *   removal even after the trips store has been cleared by navigating back to
 *   the results — which is exactly what the Departure breadcrumb does.
 *
 * Module-level rather than Redux, following the enrichment-plugin precedent
 * (see `.docs/knowledge/analytics/enrichment-plugins.md`): these are analytics
 * bookkeeping, not application state, and nothing renders from them.
 *
 * Browser-only. The server bundle imports this module too, and module state
 * there is shared by every request in the process, so a write from a server
 * render would leak one traveler's cart into another's. Nothing in the funnel
 * runs during SSR, and the guard keeps it that way.
 */

interface ReportedImpressions {
    searchKey: string;
    tripIds: Set<string>;
}

// One entry per leg. Depart and return are separate lists that can both be
// live, and replacing an entry when its search key changes keeps this bounded.
const reportedImpressions = new Map<string, ReportedImpressions>();

const cartItems = new Map<string, EcommerceItem>();

function isBrowser(): boolean {
    return typeof window !== 'undefined';
}

/**
 * The trips in `tripIds` that have not been reported for this search yet,
 * marking them reported. A new `searchKey` starts the leg over.
 */
function takeUnreportedTrips({
    legKey,
    searchKey,
    tripIds,
}: {
    legKey: string;
    searchKey: string;
    tripIds: string[];
}): string[] {
    if (!isBrowser()) {
        return tripIds;
    }

    const existing = reportedImpressions.get(legKey);
    const reported = existing && existing.searchKey === searchKey
        ? existing.tripIds
        : new Set<string>();

    reportedImpressions.set(legKey, { searchKey, tripIds: reported });

    const unreported = tripIds.filter((tripId) => !reported.has(tripId));

    unreported.forEach((tripId) => reported.add(tripId));

    return unreported;
}

function rememberCartItem(legKey: string, item: EcommerceItem): void {
    if (!isBrowser()) {
        return;
    }

    cartItems.set(legKey, item);
}

/** The leg's trip has left the cart -- rejected, or the itinerary abandoned. */
function forgetCartItem(legKey: string): void {
    cartItems.delete(legKey);
}

function getCartItem(legKey: string): EcommerceItem | undefined {
    return isBrowser()
        ? cartItems.get(legKey)
        : undefined;
}

function resetSessionState(): void {
    reportedImpressions.clear();
    cartItems.clear();
}

export {
    forgetCartItem,
    getCartItem,
    rememberCartItem,
    resetSessionState,
    takeUnreportedTrips,
};
